// Open-order card — the card-view counterpart of PositionTracker's Open
// Orders table row. Layout follows the client trader's mockup: status +
// side + venue badges, book-position badge, market header, qty/bid/last/
// queue stats, and a cost / to-win / resting-age footer.

import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { Zap } from 'lucide-react';
import { VenueChip } from '../atoms/VenueChip';
import { GameDetailLink } from '../atoms/GameDetailLink';
import { EventStateWarning } from '../atoms/EventStateWarning';
import { ExpiryBeforeStartWarning } from '../atoms/ExpiryBeforeStartWarning';
import { OrderActionButtons } from '../molecules';
import type { OrderActionButtonsProps } from '../molecules/OrderActionButtons';
import {
  AnnotatedCompactHeader,
  AnnotatedControls,
  FamilyPendingMark,
  CardShell,
  CardHeader,
  CardBody,
  CardFooter,
  CompactStat,
  PinToggle,
  StatLine,
  Pill,
  MarketTypeChip,
  SideBadge,
  BetTeamTitle,
  VelocityMuteToggle,
  SAME_GAME_HIGHLIGHT,
} from './cardKit';
import { fmtCents, fmtQty, fmtUsd, fmtAge, fmtCompactSigned } from './cardFormat';
import {
  queueBehindTone,
  queueCardTone,
  queueTone,
  queueValuePulse,
  type OrderCardVM,
  type PriceQuote,
} from './orderCardModel';
import { orderVelocity, type VelocityWindow } from './orderVelocity';
import { sideNativePriceDelta } from './priceVelocity';
import { DivergenceBadge } from './DivergenceBadge';
import { UPOddsValue, type UPOdds } from './upOdds';
import type { DivergenceInfo } from '@/lib/divergence/polyKalshiDivergence';
import { UsAccountWord, usStatusPillClass } from '@/views/PositionTracker/usTrackerRows';

/** #979 — the width reserved for the "⚡ In sale" pill on annotated cards
 *  (measured 65px at a 16px root, 69px at 20px; 4.5rem = 72px / 90px). */
const IN_SALE_SLOT = 'min-w-[4.5rem] justify-center';

/** Cents + American odds as one unit. Takes the whole {@link PriceQuote} —
 *  the two halves are derived from a single price upstream, so there is no
 *  call site that can pair a label with someone else's odds (#452). */
/** The qty label: `filled / total` once anything has filled, else the total.
 *  #1226 — a Polymarket US row shows its exact decimal text; K/P rows keep
 *  the whole-number formatter. */
export function orderQtyLabel(vm: OrderCardVM, sep: ' / ' | '/' = ' / '): string {
  if (vm.qtyText !== undefined) return vm.filling ? `${vm.filledText ?? '—'}${sep}${vm.qtyText}` : vm.qtyText;
  return vm.filling ? `${fmtQty(vm.filledQty)}${sep}${fmtQty(vm.totalQty)}` : fmtQty(vm.totalQty);
}

/** #1226 — a Polymarket US row's exact cents label (no odds); the K/P quote otherwise. */
function PriceWithOdds({ quote, text }: { quote: PriceQuote; text?: string }) {
  if (text !== undefined) return <span className="text-foreground">{text}</span>;
  const { cents, odds } = quote;
  const tone =
    cents == null
      ? 'text-muted-foreground'
      : cents > 50
        ? 'text-red-300'
        : cents < 50
          ? 'text-green-300'
          : 'text-muted-foreground';
  return (
    <span className={tone}>
      {fmtCents(cents)}
      {odds && (
        <span className="ml-1.5 text-[11px]">{odds}</span>
      )}
    </span>
  );
}

function velToneClass(d: number, filling: boolean): string {
  if (filling) return d > 0 ? 'text-amber-300' : 'text-muted-foreground';
  // Resting: a shrinking queue (negative) means the order is advancing.
  if (d < 0) return 'text-green-400';
  if (d > 0) return 'text-red-400';
  return 'text-muted-foreground';
}

const QUEUE_UNAVAILABLE_TITLE =
  'Queue data is temporarily unavailable; the order itself remains current';

const VELOCITY_WARMING_TITLE =
  'Velocity appears after one minute of visible order history';
const VELOCITY_ROW = 'flex flex-wrap items-baseline gap-x-1.5';
const VELOCITY_ROW_LABEL = 'shrink-0';

/** One labelled velocity row: its windows, `warming` before any window is
 *  covered, or `unavailable` (queue only) when there is no current reading. */
function VelocityRow({
  label,
  region,
  cells,
  fills,
}: {
  label: string;
  region: string;
  /** null = no current reading (unavailable), [] = warming. */
  cells: { label: string; d: number }[] | null;
  fills: boolean;
}) {
  return (
    <div role="group" aria-label={region} className={VELOCITY_ROW}>
      <span className={VELOCITY_ROW_LABEL}>{label}</span>
      {cells == null ? (
        <span title={QUEUE_UNAVAILABLE_TITLE}>unavailable</span>
      ) : cells.length === 0 ? (
        <span title={VELOCITY_WARMING_TITLE}>warming</span>
      ) : (
        cells.map(({ label: windowLabel, d }) => (
          <span key={windowLabel} className="whitespace-nowrap">
            <span className="text-[10px]">{windowLabel}</span>
            <span className={`ml-0.5 font-mono leading-none ${velToneClass(d, fills)}`}>
              {fills ? fmtQty(d) : fmtCompactSigned(d)}
            </span>
          </span>
        ))
      )}
    </div>
  );
}

/** Footer velocity (#1154): queue movement and fill progress as two labelled
 *  rows over the same windows, so reaching the front or filling never hides
 *  the queue. An order with no fills yet keeps the second row as an empty line,
 *  so the first fill cannot move the order actions below. */
function VelocityLine({ vm }: { vm: OrderCardVM }) {
  if (vm.venue !== 'kalshi' || (!vm.filling && vm.queue == null)) return null;
  const v = orderVelocity(vm.key);
  const windows: { label: string; w: VelocityWindow }[] = [
    { label: '1m', w: v.m1 },
    { label: '5m', w: v.m5 },
    { label: '30m', w: v.m30 },
    { label: '1h', w: v.h1 },
  ];
  const cellsOf = (pick: (w: VelocityWindow) => number | null) =>
    windows
      .map(({ label, w }) => {
        const d = pick(w);
        return d == null ? null : { label, d };
      })
      .filter((c): c is { label: string; d: number } => c !== null);
  // Gate on the card's own reading, not only the history: the history can
  // still end in a number for one poll after the queue went unavailable.
  const queueCells = vm.queue == null ? null : cellsOf((w) => w.queueDelta);
  return (
    <div className="text-muted-foreground mt-1.5 text-[11px] leading-4" data-order-velocity="">
      <VelocityRow label="queue Δ" region="Queue movement" cells={queueCells} fills={false} />
      {vm.filling ? (
        <VelocityRow
          label="filled"
          region="Fill progress"
          cells={cellsOf((w) => w.filledDelta)}
          fills
        />
      ) : (
        <div aria-hidden className={VELOCITY_ROW} data-velocity-reserve="">
          {'\u00a0'}
        </div>
      )}
    </div>
  );
}

/** #1155 — constant-width figures in the remaining line (the #615 convention):
 *  a fill that changes the digit count ($13,621.02 → $9,421.02) must not flip
 *  the line between one and two rows. Measured: without this, card widths
 *  around 308–344px (a 1000px window) changed height with the digits, which
 *  moves neighbours' actions in the Tracker's grid. Room for 999,999 contracts
 *  and $999,999.99; only crossing those magnitudes can still re-wrap. */
const REM_QTY_W = 'inline-block min-w-[7ch]';
const REM_USD_W = 'inline-block min-w-[11ch]';

/** #1155 — cost / to win for the contracts still working, beside the existing
 *  figures (which bill the filled part once anything has filled). ALWAYS
 *  rendered, like the Polymarket US order card's ordered / filled / remaining
 *  rows: before a fill it states the whole order, so a first fill changes
 *  numbers, never the card's height. In the Tracker's grids a taller card
 *  stretches its row, which drops every neighbour's Edit / Cancel (measured
 *  +23px when this line appeared only on a fill). A sell shows its proceeds
 *  and no to-win: a resting sell's profit depends on what it closes, so the
 *  line makes no promise about it. Exported for the pinned compact item. */
export function RemainingOrderLine({ vm }: { vm: OrderCardVM }) {
  return (
    <div
      role="group"
      aria-label="Remaining order"
      data-testid="order-remaining"
      className="text-muted-foreground flex flex-wrap items-baseline gap-x-1.5 text-[11px]"
    >
      <span className="whitespace-nowrap" title="Contracts still working — not yet filled">
        remaining <span className={`text-foreground font-mono ${REM_QTY_W}`}>{vm.qtyText !== undefined ? (vm.remainingText ?? '—') : fmtQty(vm.remainingShownQty)}</span>
      </span>
      {vm.isSell ? (
        <span
          className="whitespace-nowrap"
          title="What the still-working contracts would bring in at this order's price, less known maker fees"
        >
          proceeds <span className={`text-foreground font-mono ${REM_USD_W}`}>{fmtUsd(vm.remainingCostDollars)}</span>
        </span>
      ) : (
        <>
          <span
            className="whitespace-nowrap"
            title="Cost of the still-working contracts at this order's price, including known maker fees"
          >
            cost <span className={`text-foreground font-mono ${REM_USD_W}`}>{fmtUsd(vm.remainingCostDollars)}</span>
          </span>
          <span
            className="whitespace-nowrap"
            title="Net profit if the still-working contracts fill and settle favorably, less known maker fees"
          >
            to win <span className={`text-positive font-mono ${REM_USD_W}`}>{fmtUsd(vm.remainingToWinDollars)}</span>
          </span>
        </>
      )}
    </div>
  );
}

export function OrderCard({
  vm,
  onCancel,
  onEdit,
  onOpen,
  detailHref,
  canceling,
  cancelUnconfirmed = false,
  cancelTitle,
  queueCriticalFlash = false,
  queueWarningFlash = false,
  fellBehindFlash = false,
  velocitySpikeSeverity = null,
  velocityMuted = false,
  onToggleVelocityMute,
  divergence,
  divergenceFlash = false,
  upOdds,
  pinned = false,
  onTogglePin,
  annotations,
  highlighted = false,
  onMouseEnter,
  onMouseLeave,
  eventStateCertainty = 'trusted',
  viewAnchor,
  actions,
}: {
  /** Same-game highlight — true when this card's game is the hovered one. It
   *  rings the card; it must NOT touch `accentClass`, which carries the QUEUE
   *  TONE (the "am I about to get run over" channel). An earlier pass replaced
   *  the accent with the highlight, so hovering a game to check exposure
   *  silently erased the queue signal on that game's orders. */
  highlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  vm: OrderCardVM;
  /** #1226 — a Polymarket US row's action set (`usOrderActionProps`), in place
   *  of the card's own cancel/edit wiring; null = a finished US order, which
   *  shows its word and no control. Absent on K/P rows. */
  actions?: OrderActionButtonsProps | null;
  onCancel: () => void | Promise<void>;
  /** Open the EditOrderPriceModal seeded with this order's params. Parent
   *  owns the modal state — this prop just signals intent. */
  onEdit: () => void;
  onOpen?: () => void;
  /** GameDetail page URL — renders the game-detail icon when present.
   *  Card click opens the order panel; the icon is the explicit path to
   *  the detail page (and signals one exists). */
  detailHref?: string | null;
  canceling: boolean;
  /** #748 — see OrderActionButtons. Poly rows only; Kalshi leaves it false. */
  cancelUnconfirmed?: boolean;
  cancelTitle?: string;
  /** Brief one-shot cue when the order crosses into the sub-10k queue band. */
  queueCriticalFlash?: boolean;
  /**
   * Opt-in queue-warning flash (#233). Off by default, so no existing user
   * gains a cue they never had. Yields to queue-critical: the durations differ
   * (6s vs 24s), so an order can still be inside its warning flash when it
   * crosses the critical boundary, and the louder cue must win.
   */
  queueWarningFlash?: boolean;
  /** Brief one-shot cue when a new better-priced level forms above this order
   *  (queueAbove 0 -> positive), or it falls way behind. (StreamRift #178.) */
  fellBehindFlash?: boolean;
  /** Queue-velocity spike tier for this order (QueueVelocityTracker), or null
   *  when not spiking / muted. Drives the amber (warn) / red (alert) flash. */
  velocitySpikeSeverity?: 'warn' | 'alert' | null;
  /** True when the trader has muted velocity spike alerts for this order. */
  velocityMuted?: boolean;
  /** Toggle the mute above; omit to hide the control. */
  onToggleVelocityMute?: () => void;
  /** Cross-venue divergence for this market (Poly vs Kalshi), or null. The
   *  badge self-gates on threshold + poly freshness. */
  divergence?: DivergenceInfo | null;
  /** Brief one-shot cue when this market crosses into >= 2¢ divergence. */
  divergenceFlash?: boolean;
  /** Unabated consensus / Polymarket mid for this market, side-native —
   *  computed by PositionTracker's per-second sweep (position-card parity;
   *  Dan, Jul 8). '—' for spread/total/period orders (sweep is
   *  moneyline-only). */
  upOdds?: UPOdds | null;
  /** Pinned-strip membership (PositionTracker). Present onTogglePin renders
   *  the thumbtack; surfaces without a pinned strip just omit it. */
  pinned?: boolean;
  onTogglePin?: () => void;
  /** #978 — the card's annotation controls (Source, Note), in contract
   *  order, or omitted. Sits before Alarm/Pin; rows pass it only as stable
   *  children so an unchanged row still compares equal under `memo`. */
  annotations?: ReactNode;
  /** Passive lifecycle warning; never gates edit or cancel. */
  eventStateCertainty?: import('@/lib/sport/eventStateCertainty').EventStateCertainty;
  /** Stable row identity for #829's reading-position primitive, or undefined
   *  on surfaces that do not restore scroll (no attribute renders then). */
  viewAnchor?: string;
}) {
  const qTone = queueTone(vm.queue, vm.queueTotal);
  const qCardTone = queueCardTone(vm.queue, vm.queueTotal);
  const qPulse = queueValuePulse(vm.queue);

  // Ring-cue precedence. The velocity-spike, divergence and fell-behind
  // flashes all animate the SAME channel — the card's inset box-shadow — and a
  // bare `animation` shorthand fully resets the one declared before it, so at
  // most one of them may be on the card at a time. Established priority,
  // strongest first:
  //   violet divergence  >  red/amber velocity spike  >  amber fell-behind
  // Fell-behind (#178) is the gentlest of the three (it mirrors the audio
  // ranking in PositionTracker, where the fell-behind tone yields to the queue
  // warning/critical tones), so it yields to both. Resolving this here rather
  // than by cascade order keeps one readable source of truth and makes the
  // precedence testable — globals.css orders the utilities to match.
  //
  // queue-critical is NOT part of this contest: it owns the card's background
  // tint, and the paired `.animate-queue-critical-card-flash.animate-*`
  // selectors in globals.css compose it with whichever ring cue wins.
  const showFellBehindFlash =
    fellBehindFlash && !divergenceFlash && !velocitySpikeSeverity;

  // Queue-warning (#233) is a background cue like queue-critical, NOT a ring
  // cue, so it never competes with the contest above — it only yields to its
  // own louder sibling.
  const showQueueWarningFlash = queueWarningFlash && !queueCriticalFlash;

  // #979: on a card showing Source/Note the header layout must not move with
  // live status, so both status pills take the same minimum width (that of
  // "⚡ In sale"). Width only — a resting order never carries "In sale" text.
  const statusWidth = annotations != null ? ` ${IN_SALE_SLOT}` : '';
  const statusPill = vm.activeInSale ? (
    <Pill
      className={`bg-amber-400/20 text-amber-300${statusWidth}`}
      title="Order is partially filled and has no better level ahead"
    >
      <Zap size={9} /> In sale
    </Pill>
  ) : vm.us?.statusText && vm.us.statusTone ? (
    // #1226 — a Polymarket US order that is not simply resting: one amber or
    // muted word in the Resting pill's place, the full words in its hover.
    <Pill className={`${usStatusPillClass(vm.us.statusTone)}${statusWidth}`} title={vm.us.statusTitle} testId="us-order-status">
      {vm.us.statusText}
    </Pill>
  ) : (
    <Pill
      className={`bg-blue-500/20 text-blue-300${statusWidth}`}
      title="Order is resting in the book"
    >
      Resting
    </Pill>
  );

  const trailingControls = (
    <>
      {onToggleVelocityMute && (
        <VelocityMuteToggle
          muted={velocityMuted}
          onToggle={onToggleVelocityMute}
        />
      )}
      {onTogglePin && (
        <PinToggle pinned={pinned} onToggle={onTogglePin} protects={vm.venue === 'kalshi'} />
      )}
    </>
  );

  const bookPill = (() => {
    if (vm.bookDiff == null) return null;
    if (vm.bookDiff === 0) {
      return (
        <Pill
          className="bg-green-500/20 text-green-400"
          title="At the best price level; queue still shows FIFO depth at this price"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-green-400" />
          At front
        </Pill>
      );
    }
    if (vm.bookDiff === 1) {
      return (
        <Pill className="bg-yellow-500/20 text-yellow-300">
          <span className="h-1.5 w-1.5 rounded-full bg-yellow-400" />
          Behind 1¢
        </Pill>
      );
    }
    return (
      <Pill className="bg-red-500/20 text-red-300">
        <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
        Behind {vm.bookDiff}¢
      </Pill>
    );
  })();

  return (
    <CardShell
      viewAnchor={viewAnchor}
      standout={vm.activeInSale}
      accentClass={qCardTone}
      className={[
        queueCriticalFlash && 'animate-queue-critical-card-flash',
        showQueueWarningFlash && 'animate-queue-warning-card-flash',
        showFellBehindFlash && 'animate-fell-behind-card-flash',
        velocitySpikeSeverity === 'warn' && 'animate-velocity-spike-warn',
        velocitySpikeSeverity === 'alert' && 'animate-velocity-spike-alert',
        divergenceFlash && 'animate-divergence-flash',
        highlighted && SAME_GAME_HIGHLIGHT,
      ]
        .filter(Boolean)
        .join(' ')}
      title={vm.ticker}
      ariaLabel={onOpen ? `Open order book for ${vm.title}` : undefined}
      onClick={onOpen}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <CardHeader
        leftBadges={
          <>
            {statusPill}
            <span className="inline-block w-[19ch] shrink-0 text-[10px] text-muted-foreground">{vm.bookRetained ? 'Last confirmed book' : 'Book context'}</span>
            {vm.side && <SideBadge side={vm.side} team={vm.team} />}
            <MarketTypeChip marketType={vm.marketType} />
            <VenueChip venue={vm.venue} />
            {vm.us && <UsAccountWord label={vm.us.accountLabel} accountId={vm.us.accountId} />}
          </>
        }
        rightBadges={
          <>
            <EventStateWarning certainty={eventStateCertainty} display="icon" />
            {bookPill}
            <DivergenceBadge info={divergence} />
            {annotations == null && trailingControls}
          </>
        }
        // #979: only a card showing Source/Note opts into the width-based
        // second row; placement stays on row one, then Source → Note → Alarm → Pin.
        controls={annotations != null ? <>{annotations}{trailingControls}</> : undefined}
        title={
          <span className="flex items-center gap-1">
            <span className={vm.title === vm.ticker ? 'min-w-0 whitespace-normal break-all' : 'min-w-0 truncate'}>
              <BetTeamTitle title={vm.title} team={vm.titleTeam} />
            </span>
            {vm.familyPending && <FamilyPendingMark />}
            {detailHref && <GameDetailLink href={detailHref} className="-my-1" />}
          </span>
        }
        subtitle={vm.subtitle}
        wrapTitle={vm.title === vm.ticker}
      />

      {/* Event-state certainty lives as a compact header chit. This separate
          notice is arithmetic on two values we already trust; both can be true. */}
      <ExpiryBeforeStartWarning
        expiresBeforeEventStart={vm.expiresBeforeEventStart}
        className="mt-1.5"
      />

      <CardBody>
        <StatLine
          label="qty"
          value={orderQtyLabel(vm)}
        />
        <StatLine
          label={vm.isSell ? 'ask' : 'bid'}
          value={<PriceWithOdds quote={vm.bidQuote} text={vm.priceText} />}
        />
        <StatLine label="start" value={vm.eventStartLabel || '—'} />
        <StatLine
          label="last"
          value={<PriceWithOdds quote={vm.lastQuote} />}
        />
        {/* 1m price movement — side-native (NO orders flip the sign so a
            YES 50→52 reads as `-2¢` for the NO holder). Polymarket cards
            skip the chip because marketStore doesn't surface poly current
            price yet (no source to sample). Hidden until 1m of history
            has accumulated for the ticker. */}
        {vm.venue === 'kalshi' && vm.side != null && (() => {
          const delta = sideNativePriceDelta(vm.priceVelocityM1YesCents ?? null, vm.side);
          if (delta == null) return null;
          const tone =
            delta > 0
              ? 'text-green-400'
              : delta < 0
                ? 'text-red-400'
                : 'text-muted-foreground';
          return (
            <StatLine
              label="1m Δ"
              value={`${delta >= 0 ? '+' : ''}${delta}¢`}
              valueClass={tone}
            />
          );
        })()}
        {/* U/P — Unabated consensus / Polymarket mid, side-native (position
            card + table parity). UPOddsValue self-renders '—' when the sweep
            has no entry (spread/total/period orders, or pre-resolution). */}
        <StatLine label="U/P" value={<UPOddsValue odds={upOdds} />} />
        <StatLine
          label="queue"
          value={
            vm.queue == null ? '—' : vm.queue < 1 ? 'Front' : fmtQty(vm.queue)
          }
          valueClass={`${qTone} ${qPulse}`}
          title={vm.queue == null ? QUEUE_UNAVAILABLE_TITLE : undefined}
        />
        <StatLine
          label="level above"
          value={vm.queueAbove == null ? '—' : fmtQty(vm.queueAbove)}
          valueClass={queueTone(vm.queueAbove, vm.queueTotal)}
          title={vm.queueAbove == null ? QUEUE_UNAVAILABLE_TITLE : undefined}
        />
        <StatLine
          label="in line behind"
          value={vm.queueBehind == null ? '—' : fmtQty(vm.queueBehind)}
          valueClass={queueBehindTone(vm.queueBehind)}
          title={vm.queueBehind == null ? QUEUE_UNAVAILABLE_TITLE : undefined}
        />
      </CardBody>

      <CardFooter>
        <div
          className="flex items-baseline justify-between gap-2"
          title={vm.filling ? 'Filled contracts only — the remaining line below covers what is still working' : undefined}
        >
          <span className="text-muted-foreground text-xs">
            cost{' '}
            <span className="text-foreground font-mono font-semibold">
              {fmtUsd(vm.costDollars)}
            </span>
          </span>
          <span className="text-muted-foreground text-xs">
            to win{' '}
            <span className="text-positive font-mono font-semibold">
              {fmtUsd(vm.toWinDollars)}
            </span>
          </span>
        </div>
        <VelocityLine vm={vm} />
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="text-muted-foreground text-[11px]">
            {vm.tifLabel !== 'GTC' && (
              <span className="mr-2">exp {vm.tifLabel}</span>
            )}
            {vm.restingSinceMs != null && `resting ${fmtAge(vm.restingSinceMs)}`}
          </span>
          {actions !== undefined ? (actions && <OrderActionButtons {...actions} />) : (
            <OrderActionButtons
              canceling={canceling}
              unconfirmed={cancelUnconfirmed}
              cancelTitle={cancelTitle}
              onCancel={onCancel}
              onEdit={onEdit}
              editAvailable={vm.editAvailable}
            />
          )}
        </div>
        {/* #1155 — always present (see RemainingOrderLine), below the actions. */}
        <div className="mt-1.5">
          <RemainingOrderLine vm={vm} />
        </div>
      </CardFooter>
    </CardShell>
  );
}

/** Dense two-line order item — the open-orders counterpart of
 *  CompactPositionItem, built for the Pinned strip (Prof, Jul 9): header
 *  badges + title, then the stats that matter for a watched resting order
 *  (fill progress, price, last, queue, cost, to win) and the #1155 remaining
 *  line. The amber "In sale"
 *  pill carries over from OrderCard; the default "Resting" pill is dropped
 *  for space — resting is the unmarked state here. */
export function CompactOrderItem({
  vm,
  onCancel,
  onEdit,
  onOpen,
  detailHref,
  canceling,
  cancelUnconfirmed = false,
  cancelTitle,
  pinned = false,
  onTogglePin,
  annotations,
  highlighted = false,
  onMouseEnter,
  onMouseLeave,
  eventStateCertainty = 'trusted',
  viewAnchor,
  actions,
}: {
  vm: OrderCardVM;
  /** #1226 — see OrderCard. */
  actions?: OrderActionButtonsProps | null;
  onCancel: () => void | Promise<void>;
  onEdit: () => void;
  onOpen?: () => void;
  detailHref?: string | null;
  canceling: boolean;
  /** #748 — see OrderActionButtons. Poly rows only; Kalshi leaves it false. */
  cancelUnconfirmed?: boolean;
  cancelTitle?: string;
  pinned?: boolean;
  onTogglePin?: () => void;
  /** #978 — the card's annotation controls (Source, Note), in contract
   *  order, or omitted. Sits before Alarm/Pin; rows pass it only as stable
   *  children so an unchanged row still compares equal under `memo`. */
  annotations?: ReactNode;
  /** Same-game highlight — true when this row's game is the hovered one. */
  highlighted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Passive lifecycle warning; never gates edit or cancel. */
  eventStateCertainty?: import('@/lib/sport/eventStateCertainty').EventStateCertainty;
  /** Stable row identity for #829's reading-position primitive, or undefined
   *  on surfaces that do not restore scroll (no attribute renders then). */
  viewAnchor?: string;
}) {
  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!onOpen || e.currentTarget !== e.target) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      data-card-root=""
      data-view-anchor={viewAnchor}
      title={vm.ticker}
      role={onOpen ? 'link' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `Open order book for ${vm.title}` : undefined}
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`bg-card px-2.5 py-2 transition-colors hover:bg-muted/20 ${
        onOpen ? 'cursor-pointer focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400/70' : ''
      } ${highlighted ? SAME_GAME_HIGHLIGHT : ''}`}
    >
      <AnnotatedCompactHeader annotated={annotations != null} layout="compact-order">
      <div className={`flex min-w-0 flex-wrap items-center gap-1.5${annotations != null ? ' g1-annotated-header__row' : ''}`}>
        {vm.activeInSale ? (
          <Pill
            className={`bg-amber-400/20 text-amber-300${annotations != null ? ` ${IN_SALE_SLOT}` : ''}`}
            title="Order is partially filled and has no better level ahead"
          >
            <Zap size={9} /> In sale
          </Pill>
        ) : (
          // #979: an annotated header reserves the pill's width (an EMPTY box —
          // never "In sale" text on a resting order) so the row cannot re-wrap
          // when the order goes in or out of sale.
          annotations != null && <span aria-hidden className={`inline-block h-px ${IN_SALE_SLOT}`} />
        )}
        {vm.side && (
          <SideBadge
            side={vm.side}
            team={vm.team}
            className="max-w-[min(40%,10rem)] shrink"
          />
        )}
        <VenueChip venue={vm.venue} />
        {vm.us && <UsAccountWord label={vm.us.accountLabel} accountId={vm.us.accountId} />}
        {/* #1226 — a US order's one-word mark, where the In-sale pill sits. */}
        {vm.us?.statusText && vm.us.statusTone && (
          <Pill className={usStatusPillClass(vm.us.statusTone)} title={vm.us.statusTitle} testId="us-order-status">{vm.us.statusText}</Pill>
        )}
        <div className="min-w-[8rem] flex-1">
          <div className="text-foreground flex items-center gap-1 text-xs font-semibold leading-tight">
            <span className={vm.title === vm.ticker ? 'min-w-0 whitespace-normal break-all' : 'min-w-0 truncate'}>
              <BetTeamTitle title={vm.title} team={vm.titleTeam} />
            </span>
            {vm.familyPending && <FamilyPendingMark />}
            {detailHref && <GameDetailLink href={detailHref} className="-my-1" />}
          </div>
          {vm.subtitle && (
            <div className="text-muted-foreground truncate font-mono text-[10px] leading-tight">
              {vm.subtitle}
            </div>
          )}
        </div>
        <EventStateWarning certainty={eventStateCertainty} display="icon" />
        <AnnotatedControls annotated={annotations != null}>
          {annotations}
          {onTogglePin && (
            <PinToggle pinned={pinned} onToggle={onTogglePin} protects={vm.venue === 'kalshi'} />
          )}
          {actions !== undefined ? (actions && <OrderActionButtons {...actions} />) : (
            <OrderActionButtons
              canceling={canceling}
              unconfirmed={cancelUnconfirmed}
              cancelTitle={cancelTitle}
              onCancel={onCancel}
              onEdit={onEdit}
              editAvailable={vm.editAvailable}
            />
          )}
        </AnnotatedControls>
      </div>
      </AnnotatedCompactHeader>

      <div className="h-3 text-[10px] text-muted-foreground">{vm.bookRetained ? 'Last confirmed book' : 'Book context'}</div>
      <div className="mt-1.5 grid grid-cols-3 gap-x-2 gap-y-1 sm:grid-cols-6">
        <CompactStat
          label="qty"
          value={orderQtyLabel(vm, '/')}
          valueClass={vm.filling ? 'text-amber-300' : 'text-foreground'}
        />
        <CompactStat
          label={vm.isSell ? 'ask' : 'bid'}
          value={<PriceWithOdds quote={vm.bidQuote} text={vm.priceText} />}
        />
        <CompactStat
          label="last"
          value={<PriceWithOdds quote={vm.lastQuote} />}
        />
        <CompactStat
          label="queue"
          value={
            vm.queue == null ? '—' : vm.queue < 1 ? 'Front' : fmtQty(vm.queue)
          }
          valueClass={`${queueTone(vm.queue, vm.queueTotal)} ${queueValuePulse(vm.queue)}`}
          title={vm.queue == null ? QUEUE_UNAVAILABLE_TITLE : undefined}
        />
        <CompactStat label="cost" value={fmtUsd(vm.costDollars)} />
        <CompactStat
          label="to win"
          value={fmtUsd(vm.toWinDollars)}
          valueClass="text-positive"
        />
      </div>
      {/* #1155 — always present (see RemainingOrderLine). */}
      <div className="mt-1">
        <RemainingOrderLine vm={vm} />
      </div>
    </div>
  );
}
